// src/components/dialog/BoundaryMatrix.jsx
import { Box, Typography, Button, Chip, alpha, CircularProgress } from "@mui/material";
import { useMemo } from "react";
import { FiCheck, FiX, FiGrid } from "react-icons/fi";
import { useTheme } from "@mui/material/styles";

export default function BoundaryMatrix({
  fields,
  categories,
  selectedMatrix,
  onMatrixChange,
  loading = false,
  searchTerm = "",
}) {
  const theme = useTheme();

  const handleMatrixToggle = (field, category) => {
    const current = selectedMatrix[field] || [];
    const updated = current.includes(category)
      ? current.filter((c) => c !== category)
      : [...current, category];
    
    onMatrixChange({
      ...selectedMatrix,
      [field]: updated,
    });
  };

  const handleSelectAllRow = (field) => {
    const allSelected = (selectedMatrix[field] || []).length === categories.length;
    onMatrixChange({
      ...selectedMatrix,
      [field]: allSelected ? [] : [...categories],
    });
  };

  const handleSelectAllColumn = (category) => {
    const allSelected = fields.every((field) =>
      selectedMatrix[field]?.includes(category)
    );
    const updated = { ...selectedMatrix };

    fields.forEach((field) => {
      const current = updated[field] || [];
      if (allSelected) {
        updated[field] = current.filter((c) => c !== category);
      } else {
        updated[field] = [...new Set([...current, category])];
      }
    });

    onMatrixChange(updated);
  };

  const handleSelectAllGlobal = () => {
    const allSelected = fields.every(
      (field) => (selectedMatrix[field] || []).length === categories.length
    );

    if (allSelected) {
      const emptyMatrix = {};
      fields.forEach(field => { emptyMatrix[field] = []; });
      onMatrixChange(emptyMatrix);
    } else {
      const fullMatrix = {};
      fields.forEach(field => { fullMatrix[field] = [...categories]; });
      onMatrixChange(fullMatrix);
    }
  };

  const totalSelections = useMemo(() => {
    return Object.values(selectedMatrix).reduce(
      (sum, cats) => sum + (cats?.length || 0), 0
    );
  }, [selectedMatrix]);

  const selectedCategories = useMemo(() => {
    const set = new Set();
    Object.values(selectedMatrix).forEach((cats) => {
      cats.forEach((c) => set.add(c));
    });
    return Array.from(set);
  }, [selectedMatrix]);

  const filteredFields = useMemo(() => {
    if (!searchTerm) return fields;
    return fields.filter(f => f.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [fields, searchTerm]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">No boundary categories available</Typography>
      </Box>
    );
  }

  if (filteredFields.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">
          {searchTerm ? `No fields matching "${searchTerm}"` : "No fields available. Please ensure script file has Scenario Outline with Examples."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        borderRadius: 2, 
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      <Box 
        sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {filteredFields.length} fields × {categories.length} categories
          </Typography>
          <Chip
            label={`${totalSelections} selections`}
            size="small"
            sx={{ height: 24 }}
          />
        </Box>
        
        <Button
          onClick={handleSelectAllGlobal}
          size="small"
          variant="contained"
          startIcon={fields.every(field => (selectedMatrix[field] || []).length === categories.length) ? <FiX /> : <FiCheck />}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            backgroundColor: fields.every(field => (selectedMatrix[field] || []).length === categories.length)
              ? theme.palette.error.main
              : theme.palette.primary.main,
            '&:hover': {
              backgroundColor: fields.every(field => (selectedMatrix[field] || []).length === categories.length)
                ? theme.palette.error.dark
                : theme.palette.primary.dark,
            },
          }}
        >
          {fields.every(field => (selectedMatrix[field] || []).length === categories.length) ? "Deselect All" : "Select All"}
        </Button>
      </Box>

      {selectedCategories.length > 0 && (
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, backgroundColor: alpha(theme.palette.primary.main, 0.03) }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedCategories.slice(0, 4).map((cat, idx) => (
              <Chip
                key={idx}
                label={cat.replace(/_/g, " ")}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  height: 24,
                }}
              />
            ))}
            {selectedCategories.length > 4 && (
              <Chip
                label={`+${selectedCategories.length - 4} more`}
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ overflowX: 'auto', maxHeight: '500px' }}>
        <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
              <th 
                style={{ 
                  position: 'sticky', 
                  top: 0,
                  left: 0,
                  zIndex: 20,
                  backgroundColor: theme.palette.background.paper,
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  minWidth: '180px',
                }}
              >
                FIELD NAME
              </th>
              {categories.map((cat) => {
                const isColumnChecked = filteredFields.every(
                  (field) => selectedMatrix[field]?.includes(cat),
                );
                const selectedCount = filteredFields.filter(field => selectedMatrix[field]?.includes(cat)).length;
                
                return (
                  <th 
                    key={cat} 
                    style={{ 
                      position: 'sticky',
                      top: 0,
                      backgroundColor: theme.palette.background.paper,
                      padding: '12px 8px',
                      textAlign: 'center',
                      minWidth: '100px',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Button
                        onClick={() => handleSelectAllColumn(cat)}
                        size="small"
                        variant={isColumnChecked ? "contained" : "outlined"}
                        sx={{
                          minWidth: 32,
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          p: 0,
                          backgroundColor: isColumnChecked ? theme.palette.primary.main : 'transparent',
                          '&:hover': {
                            backgroundColor: isColumnChecked ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1),
                          },
                        }}
                      >
                        {isColumnChecked ? (
                          <FiCheck size={14} />
                        ) : selectedCount > 0 ? (
                          <Typography variant="caption" fontWeight={600}>{selectedCount}</Typography>
                        ) : (
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: alpha(theme.palette.primary.main, 0.3) }} />
                        )}
                      </Button>
                      <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                        {cat.replace(/_/g, " ")}
                      </Typography>
                    </Box>
                  </th>
                );
              })}
              <th 
                style={{ 
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                  padding: '12px 8px',
                  textAlign: 'center',
                  minWidth: '80px',
                }}
              >
                <Typography variant="caption" fontWeight={600} color="primary">
                  ALL
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredFields.map((field) => {
              const isFullySelected = (selectedMatrix[field] || []).length === categories.length;
              const isPartiallySelected = (selectedMatrix[field] || []).length > 0 && !isFullySelected;
              
              return (
                <tr 
                  key={field}
                  style={{ 
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
                    backgroundColor: isFullySelected 
                      ? alpha(theme.palette.success.main, 0.05)
                      : isPartiallySelected
                      ? alpha(theme.palette.primary.main, 0.03)
                      : 'transparent',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <td 
                    style={{ 
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.background.paper,
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      minWidth: '180px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FiGrid size={14} style={{ color: isPartiallySelected || isFullySelected ? theme.palette.primary.main : theme.palette.text.disabled }} />
                      <Typography variant="body2">{field}</Typography>
                    </Box>
                  </td>
                  
                  {categories.map((cat) => {
                    const isChecked = selectedMatrix[field]?.includes(cat) || false;
                    return (
                      <td key={cat} style={{ padding: '8px', textAlign: 'center' }}>
                        <Button
                          onClick={() => handleMatrixToggle(field, cat)}
                          size="small"
                          variant={isChecked ? "contained" : "outlined"}
                          sx={{
                            minWidth: 32,
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            p: 0,
                            backgroundColor: isChecked ? theme.palette.primary.main : 'transparent',
                            borderColor: isChecked ? 'transparent' : alpha(theme.palette.primary.main, 0.2),
                            '&:hover': {
                              backgroundColor: isChecked ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          {isChecked ? (
                            <FiCheck size={14} style={{ color: 'white' }} />
                          ) : (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: alpha(theme.palette.primary.main, 0.3) }} />
                          )}
                        </Button>
                      </td>
                    );
                  })}
                  
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <Button
                      onClick={() => handleSelectAllRow(field)}
                      size="small"
                      variant="outlined"
                      sx={{
                        minWidth: 32,
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        p: 0,
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        backgroundColor: isFullySelected ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      {isFullySelected ? (
                        <FiX size={14} style={{ color: theme.palette.error.main }} />
                      ) : (
                        <FiCheck size={14} style={{ color: isPartiallySelected ? theme.palette.primary.main : theme.palette.text.disabled }} />
                      )}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}